/**
 * SessionQuotaIndicator — pastille compteur dans le header.
 *
 * Code couleur :
 *   - vert tant que < 50% du quota
 *   - jaune entre 50% et 80%
 *   - orange entre 80% et 100%
 *   - rouge si épuisé (avec animation pulse pour attirer l'œil)
 *
 * Cliquable → emmène vers Mon compte (section quota détaillé).
 *
 * Caché pour les users Kestio internes (bypass total) et en mode staging/dev.
 *
 * Cf. spec/rate-limiter-sessions.md §3.1
 */
import { useQuota } from "../hooks/useQuota";
import { useTranslation } from "react-i18next";

export function SessionQuotaIndicator({
  onClick,
  onBuyPack,
}: {
  onClick?: () => void;
  onBuyPack?: () => void;
}) {
  const { quota, loading } = useQuota();
  const { t } = useTranslation();

  if (loading || !quota) return null;
  if (quota.isKestioInternal) return null;

  // Crédits packs one-shot (compteur global compte) — affichés même sans abo,
  // et même sur staging/dev (info d'achat, indépendante du bypass rate-limit).
  const packTotal = quota.oneShotCredits?.total ?? 0;
  const packRemaining = quota.oneShotCredits?.remaining ?? 0;
  const packPill = packTotal > 0 ? (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-violet-100 text-violet-800 hover:bg-violet-200 text-sm font-bold ring-1 ring-violet-300 transition-colors"
      title={t("packs.creditsTitle", { remaining: packRemaining, total: packTotal })}
    >
      <span>🎟️</span>
      <span>{packRemaining}</span>
    </button>
  ) : null;

  // Quota abo : masqué sur staging/dev (rate-limit bypassé) et si pas d'abo.
  if (quota.envMode === "staging" || quota.limit == null) return packPill;

  // Total disponible = limite plan + packs legacy + crédits packs one-shot
  // (la conso bascule sur le pack quand l'abo est épuisé — cf. spec packs §2.4)
  const totalAvailable = (quota.limit ?? 0) + quota.totalPacksRemaining + packRemaining;
  const ratio = totalAvailable > 0 ? quota.used / totalAvailable : 1;
  const exhausted = ratio >= 1;

  let colorClasses = "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 ring-emerald-300"; // vert < 50%
  if (exhausted) {
    colorClasses = "bg-red-100 text-red-800 hover:bg-red-200 ring-red-400 animate-pulse"; // rouge épuisé
  } else if (ratio >= 0.8) {
    colorClasses = "bg-orange-100 text-orange-800 hover:bg-orange-200 ring-orange-300"; // orange > 80%
  } else if (ratio >= 0.5) {
    colorClasses = "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 ring-yellow-300"; // jaune > 50%
  }

  // Période de grâce 14j → badge "Découverte" spécifique
  if (quota.gracePeriodActive) {
    return (
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 text-sm font-bold ring-1 ring-blue-300 transition-colors"
          title={t("packs.discoveryTitle")}
        >
          <span>🎁</span>
          <span>{t("packs.discoveryBadge")}</span>
        </button>
        {packPill}
      </div>
    );
  }

  const packLabel = quota.totalPacksRemaining > 0 ? ` (+${quota.totalPacksRemaining})` : "";
  // `onBuyPack` n'est fourni qu'aux acheteurs (`canBuyLicences`, cf. Layout) : l'infobulle ne
  // promet un achat qu'à eux (Q3 du 29-30/09/2026).
  const titleText = exhausted
    ? t(onBuyPack ? "quota.indicatorExhaustedBuyer" : "quota.indicatorExhausted", { used: quota.used, limit: quota.limit })
    : t("quota.indicatorUsed", { count: quota.used, limit: quota.limit, packs: packLabel, period: t(quota.envMode === "demo" ? "quota.periodDemo" : "quota.periodMonth") });

  // Quand quota épuisé : clic → destination d'achat (`ouvrirAchatDePack`, la même partout) au
  // lieu de "Mon compte", pour offrir un raccourci immédiat au CTA de remédiation.
  const handleClick = exhausted && onBuyPack ? onBuyPack : onClick;

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-bold ring-1 transition-colors ${colorClasses}`}
        title={titleText}
      >
        <span>{quota.used}/{quota.limit}{packLabel}</span>
        {exhausted && <span className="text-xs">⚠️</span>}
      </button>
      {packPill}
    </div>
  );
}
